import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation
} from '@angular/core';
import type { Connection1cFindInterface } from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-find.dto';
import { BasisSystem1cService } from '@zaoblako/web/core/services/basis/basis-system-1c.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ImageComponent } from '@zaoblako/web/shared/dumbs/image/image.component';
import { SharedModule } from 'primeng/api';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { Subscription } from 'rxjs';

type ImageType = {
  src: string;
  alt: string;
  isShowForm1C?: boolean;
  isShowNext?: boolean;
  isShowExtension?: boolean;
};

@Component({
  selector: 'crm-newsletter-welcome-system-connect-1c',
  standalone: true,
  styleUrls: ['./newsletter-welcome-system-connect-1c.component.scss'],
  imports: [CommonModule, ButtonComponent, SharedModule, ImageComponent],
  templateUrl: './newsletter-welcome-system-connect-1c.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class NewsletterWelcomeSystemConnect1cComponent implements OnDestroy {
  public loading = false;
  @Input() public databases: Connection1cFindInterface[] = [];

  @Output() public next = new EventEmitter<void>();

  public loadingDownload = false;

  public images1C: ImageType[] = [
    {
      src: 'assets/images/welcome-system/1c/1.png',
      alt: '1'
    },
    {
      src: 'assets/images/welcome-system/1c/2.png',
      alt: '2'
    },
    {
      src: 'assets/images/welcome-system/1c/3.png',
      alt: '3',
      isShowExtension: true
    },
    {
      src: 'assets/images/welcome-system/1c/4.png',
      alt: '4'
    },
    {
      src: 'assets/images/welcome-system/1c/5.png',
      alt: '5'
    },
    {
      src: 'assets/images/welcome-system/1c/6.png',
      alt: '6'
    },
    {
      src: 'assets/images/welcome-system/1c/7.png',
      alt: '7'
    },
    {
      src: 'assets/images/welcome-system/1c/8.png',
      alt: '8'
    },
    {
      src: 'assets/images/welcome-system/1c/9.png',
      alt: '9'
    },
    {
      src: 'assets/images/welcome-system/1c/10.png',
      alt: '10'
    },
    {
      src: 'assets/images/welcome-system/1c/11.png',
      alt: '11'
    },
    {
      src: 'assets/images/welcome-system/1c/12.png',
      alt: '12'
    },
    {
      src: 'assets/images/welcome-system/1c/13.png',
      alt: '13'
    },
    {
      src: 'assets/images/welcome-system/1c/14.png',
      alt: '14',
      isShowForm1C: true
    },
    {
      src: 'assets/images/welcome-system/1c/15.png',
      alt: '15'
    },
    {
      src: 'assets/images/welcome-system/1c/16.png',
      alt: '16'
    },
    {
      src: 'assets/images/welcome-system/1c/17.png',
      alt: '17'
    },
    {
      src: 'assets/images/welcome-system/1c/18.png',
      alt: '18'
    },
    {
      src: 'assets/images/welcome-system/1c/19.png',
      alt: '19',
      isShowNext: true
    }
  ];

  public activeImageIndex = 0;
  public activeImage: ImageType = this.images1C[0];

  private formSubscription$: Subscription = new Subscription();

  public get isShowNextButton(): boolean {
    return !!this.images1C[this.activeImageIndex + 1];
  }

  public get isShowBackButton(): boolean {
    return this.activeImageIndex !== 0;
  }

  public constructor(
    private readonly basisSystem1cService: BasisSystem1cService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnDestroy() {
    this.formSubscription$?.unsubscribe();
  }

  public nextImage() {
    if (this.activeImage.isShowForm1C && !this.databases?.length) {
      return this.errorService.addCustomError('Заполните сперва реквизиты 1С');
    }

    this.activeImageIndex = this.images1C[this.activeImageIndex + 1]
      ? this.activeImageIndex + 1
      : this.activeImageIndex;
    this.activeImage = this.images1C[this.activeImageIndex];
  }

  public backImage() {
    this.activeImageIndex = this.activeImageIndex !== 0 ? this.activeImageIndex - 1 : this.activeImageIndex;
    this.activeImage = this.images1C[this.activeImageIndex];
  }

  public downloadExtension() {
    this.loadingDownload = true;
    this.cdRef.markForCheck();

    this.basisSystem1cService.download().subscribe((data) => {
      const file = new Blob([data], { type: 'application/octet-stream' });
      let url = URL.createObjectURL(file);
      let a = document.createElement('a');
      document.body.appendChild(a);
      a.setAttribute('style', 'display: none');
      a.href = url;
      a.download = 'ЗаФинансы.cfe';
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
      this.loadingDownload = false;
      this.cdRef.markForCheck();
    });
  }

  public showAddDatabase() {
    const result = this.formStoreService.addForm<Connection1cFindInterface>(TypeFormEnum.DATABASE_1C, {
      headerText: 'Создание базы 1С',
      widthSize: 'form-sidebar-small',
      isNotShowRedirectInCreate: true,
      typeZIndex: 'z-index-2000',
      data: {
        isWelcomeSystem: true
      }
    });

    this.formSubscription$.add(
      result.onSave.subscribe((database) => {
        this.databases.push(database);
        this.databases = [...this.databases];
        this.cdRef.markForCheck();
      })
    );
  }
}
